import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  trackingId?: string;
  trackingCategory?: string;
  trackingAction?: string;
  trackingLabel?: string;
}

interface AnalyticsWindow extends Window {
  analytics?: {
    trackEvent: (data: any) => void;
  }
}

const buttonVariants = {
  primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm',
  secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 shadow-sm',
  outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500',
  ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm',
  success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 shadow-sm'
};

const buttonSizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
  xl: 'px-8 py-4 text-lg'
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  iconPosition = 'left',
  trackingId,
  trackingCategory = 'ui',
  trackingAction = 'click',
  trackingLabel,
  children,
  className,
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  onClick,
  ...props
}) => {
  const isDisabled = disabled || loading;
  
  // Handle click with tracking
  const handleClick = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent double-clicks during loading
    if (loading) {
      e.preventDefault();
      return;
    }
    
    // Track click if trackingId is provided
    if (trackingId && typeof window !== 'undefined') {
      // Import analytics dynamically to avoid SSR issues
      import('@/utils/analytics').then(({ analytics }) => {
        analytics.trackEvent({
          action: trackingAction as any,
          category: trackingCategory as any,
          label: trackingLabel || trackingId,
          custom_parameters: {
            button_id: trackingId,
            page_path: window.location.pathname
          }
        });
      }).catch(error => {
        console.warn('Analytics tracking failed:', error);
      });
      }
    
    // Call original onClick handler
    onClick?.(e);
  }, [onClick, trackingId, trackingCategory, trackingAction, trackingLabel, loading]);

  return (
    <button
      type={type}
      className={cn(
        // Base styles
        'inline-flex items-center justify-center font-medium rounded-lg',
        'transition-all duration-200 ease-in-out',
        'focus:outline-none focus:ring-2 focus:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        'active:scale-95',
        // Variant styles
        buttonVariants[variant],
        // Size styles
        buttonSizes[size],
        className
      )}
      disabled={isDisabled}
      aria-label={ariaLabel}
      aria-busy={loading}
      onClick={handleClick}
      {...props}
    >
      {loading && (
        <Loader2 className="w-4 h-4 mr-2 animate-spin\" aria-hidden="true" />
      )}
      
      {!loading && icon && iconPosition === 'left' && (
        <span className="mr-2\" aria-hidden="true">{icon}</span>
      )}
      
      {children}
      
      {!loading && icon && iconPosition === 'right' && (
        <span className="ml-2" aria-hidden="true">{icon}</span>
      )}
    </button>
  );
};

export default Button;